/* eslint-disable no-param-reassign */
import { PayloadAction } from '@reduxjs/toolkit';
import { differenceInYears, parse } from 'date-fns';
import { ArrayHelpers, FieldArrayRenderProps, FormikProps } from 'formik';
import i18next, { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BusinessCustomerTypesEnum } from '../../screens/NewQuotation/CustomerDataPage/customerDataPage.model';
import { CustomerTypeEnum } from '../../types/customerTypeSelection';
import { FlowTypeEnum, NoYesEnum } from '../../types/forms';
import { ProductFamilyEnum } from '../../types/productFamilySelection';
import { ReduxTrackedForm, TrackedFormInfo } from '../../types/reduxTrackedForm';
import { journeyDataSelectors } from '../redux/features/journeyData.slice';

export type FormUpdateActionPayload<
  FormState extends { forms: Record<any, ReduxTrackedForm<Record<string, any>>> },
  FormName = keyof FormState['forms'],
  T = FormState['forms'][FormName] extends ReduxTrackedForm<infer ValuesType> ? ValuesType : any,
> = {
  formName: FormName;
  values?: ReduxTrackedForm<T>['values'];
  valuesPartial?: Partial<ReduxTrackedForm<T>['values']>;
  isValid?: ReduxTrackedForm<T>['isValid'];
  dirty?: ReduxTrackedForm<T>['dirty'];
  isValidating?: ReduxTrackedForm<T>['isValidating'];
  isSubmitting?: ReduxTrackedForm<T>['isSubmitting'];
  triggerValidation?: boolean;
  triggerReset?: boolean;
  triggerUiUpdate?: boolean;
  submitCount?: ReduxTrackedForm<T>['submitCount'];
  excluded?: ReduxTrackedForm<T>['excluded'];
  reset?: boolean;
  /**
   * - Prevent excluded form from loosing current values.
   * - Works only for this action.
   * - Use freezeValues on form to prevent any exclusion action
   * from changing the values.
   */
  keepValues?: boolean;
  /**
   * - As long as set to true exclusion won't reset form values.
   */
  freezeValues?: ReduxTrackedForm<T>['freezeValues'];
};

export type FormUpdateActionType<FormState extends { forms: Record<any, ReduxTrackedForm<Record<string, any>>> }> =
  PayloadAction<FormUpdateActionPayload<FormState>>;

export type FormUpdateActionTypeGeneric<
  FormState extends { forms: Record<any, ReduxTrackedForm<Record<string, any>>> } = any,
> = PayloadAction<Omit<FormUpdateActionPayload<FormState>, 'formName'>>;

/**
 * - Form updater action that each form integrated with redux should have.
 */
export const getFormUpdater = <FormState extends { forms: Record<any, ReduxTrackedForm<any>> }>() => {
  return (state: FormState, action: FormUpdateActionType<FormState>) => {
    if (action.payload.values !== undefined && !state.forms[action.payload.formName].excluded) {
      state.forms[action.payload.formName].values = action.payload.values;
    }
    if (action.payload.valuesPartial !== undefined && !state.forms[action.payload.formName].excluded) {
      state.forms[action.payload.formName].values = {
        ...state.forms[action.payload.formName].values,
        ...action.payload.valuesPartial,
      };
    }
    if (action.payload.isValid !== undefined) {
      state.forms[action.payload.formName].isValid = action.payload.isValid;
    }
    if (action.payload.dirty !== undefined) {
      state.forms[action.payload.formName].dirty = action.payload.dirty;
    }
    if (action.payload.isValidating !== undefined) {
      state.forms[action.payload.formName].isValidating = action.payload.isValidating;
    }
    if (action.payload.isSubmitting !== undefined) {
      state.forms[action.payload.formName].isSubmitting = action.payload.isSubmitting;
    }
    if (action.payload.submitCount !== undefined) {
      state.forms[action.payload.formName].submitCount = action.payload.submitCount;
    }
    if (action.payload.triggerValidation !== undefined && !state.forms[action.payload.formName].excluded) {
      state.forms[action.payload.formName].triggers.validation = action.payload.triggerValidation;

      // If validation should be triggered deem it as started right away.
      if (action.payload.triggerValidation) {
        state.forms[action.payload.formName].isValidating = true;
      }
    }
    if (action.payload.triggerReset !== undefined) {
      state.forms[action.payload.formName].triggers.reset = action.payload.triggerReset;

      if (!action.payload.triggerReset) {
        /*  */
      } else if (state.forms[action.payload.formName].initialValues) {
        state.forms[action.payload.formName].values = {
          ...state.forms[action.payload.formName].initialValues,
        };
      } else {
        throw new Error(
          `Form ${String(
            action.payload.formName
          )} has no initialValues to reset to. Check createReduxTrackedFormDefaults and if initialValues are passed.`
        );
      }
    }
    if (action.payload.freezeValues !== undefined) {
      state.forms[action.payload.formName].freezeValues = action.payload.freezeValues;
    }
    if (action.payload.excluded !== undefined) {
      state.forms[action.payload.formName].excluded = action.payload.excluded;

      if (action.payload.excluded) {
        state.forms[action.payload.formName].excluded = true;
        state.forms[action.payload.formName].isValid = true;
        state.forms[action.payload.formName].isSubmitting = false;
        state.forms[action.payload.formName].isValidating = false;
        state.forms[action.payload.formName].dirty = false;
        state.forms[action.payload.formName].submitCount = 0;
      }
    }
    if (action.payload.triggerUiUpdate !== undefined) {
      state.forms[action.payload.formName].triggers.uiUpdate = action.payload.triggerUiUpdate;
    }
  };
};

export const getFormExcluder = <FormState extends { forms: Record<any, ReduxTrackedForm<Record<string, any>>> }>() => {
  return (
    state: FormState,
    action: PayloadAction<Pick<FormUpdateActionPayload<FormState>, 'formName' | 'keepValues'>>
  ) => {
    state.forms[action.payload.formName].excluded = true;
    state.forms[action.payload.formName].isValid = true;
    state.forms[action.payload.formName].isSubmitting = false;
    state.forms[action.payload.formName].isValidating = false;
    state.forms[action.payload.formName].dirty = false;
    state.forms[action.payload.formName].submitCount = 0;

    if (action.payload.keepValues) return;
    if (state.forms[action.payload.formName].initialValues) {
      state.forms[action.payload.formName].values = {
        ...state.forms[action.payload.formName].initialValues,
      };
    } else {
      throw new Error(
        `Form ${String(
          action.payload.formName
        )} has no initialValues to reset to. Check createReduxTrackedFormDefaults and if initialValues are passed.`
      );
    }
  };
};

export const getFormsState = <FTO extends Record<string, ReduxTrackedForm<Record<string, any>>>>(
  formsToObserve: FTO
) => {
  return Object.entries(formsToObserve).reduce(
    (stateSummary, [formName, trackedForm]) => {
      return {
        ...stateSummary,
        [formName]: {
          isSubmitting: trackedForm.isSubmitting,
          isValidating: trackedForm.isValidating,
          isValid: trackedForm.isValid,
          dirty: trackedForm.dirty,
          submitCount: trackedForm.submitCount,
          excluded: trackedForm.excluded,
        },
      };
    },
    {} as { [k in keyof FTO]: TrackedFormInfo<FTO[k]> }
  );
};

/**
 * - Pass form state and receive dictionary of field names.
 * - Example return: `{ firstName: 'firstName' }`
 */
export const getFormFieldNames = <FormModel extends Record<string, any>>(formState: ReduxTrackedForm<FormModel>) => {
  return Object.keys(formState.values).reduce(
    (namesObject, name) => {
      return {
        ...namesObject,
        [name]: name,
      };
    },
    // Without Required<...> type will result in 'someFieldName' | undefined values.
    {} as { [k in keyof Required<FormModel>]: k }
  );
};

/**
 * - Objects representing TS enums can have some extra keys for fields with numeric values,
 * so we filter them out.
 */
export const getEnumKeys = (optionsEnum: Record<any, any>) => {
  return Object.keys(optionsEnum).filter((key) => Number.isNaN(Number(key)));
};

export const getEnumValues = <E extends Record<any, any>>(optionsEnum: E): Array<E[keyof E]> => {
  return getEnumKeys(optionsEnum).map((key) => optionsEnum[key]);
};

export const getEnumValuesNullable = <E extends Record<any, any>>(
  optionsEnum: E
): Array<E[keyof E] | null | undefined> => {
  return [...getEnumKeys(optionsEnum).map((key) => optionsEnum[key]), '', null, undefined];
};

export const getPlaceholderOption = (translationFile: string, fieldName: string, fieldsGroupKey: string = 'fields') => {
  const placeholderKey = `${translationFile}:${fieldsGroupKey}:${fieldName}:placeholder`;
  const placeholderLabel = t(placeholderKey);

  if (!i18next.exists(placeholderKey)) {
    throw new Error(`No placeholder translation for the select input was defined (${placeholderKey} not resolved). `);
  }

  return {
    label: placeholderLabel,
    value: '',
  };
};

/**
 * Moves Ireland (+353) to the first position in the options list.
 * All other options keep their original order.
 */
export const prioritizeIreland = (options: Array<{ label: string; value: any }>) => {
  return [
    ...options.filter((options) => options.value === '+353'),
    ...options.filter((options) => options.value !== '+353'),
  ];
};

/**
 * - Provide harmonized* translation and options enum to receive list of options for SelectFormField.
 * - Harmonized meaning that each enum field maps 1:1 to specific translation key defined under translationFile:options:filedName.
 * For example:
 * - `model.ts -> enum FieldAEnum { value1: 'Value 1' }`
 * - `myFile.json -> { options: { 'fieldA': { value1: 'Value 1 Label' } } }`
 * - You then pass `FieldAEnum` as optionsEnum, `myFile` as translationFile and 'fieldA' as fieldName.
 */
export const createOptionsList = <OE extends Record<any, any>>(
  optionsEnum: OE,
  translationFile: string,
  fieldName: string,
  placeholderGroupKey: string | null = 'fields'
): Array<{ label: string; value: any }> => {
  // Objects representing TS enums can have some extra keys for fields with numeric values,
  // so we filter them out.
  const keys = getEnumKeys(optionsEnum);

  const getTranslation = (key: string) => {
    // Looking for enum translations in enum.json.
    let translationKey = `enum:${fieldName}:${key}`;
    let text = t(translationKey);
    // i18n will return translationKey as translation value if key was not found.
    if (i18next.exists(translationKey)) {
      return text;
    }

    // Look for enum translation in dedicated file
    translationKey = `${translationFile}:options:${fieldName}:${key}`;
    text = t(translationKey);

    if (!i18next.exists(translationKey)) {
      throw new Error(`Translation key (${translationKey}) not resolved. `);
    }

    return text;
  };

  const options = keys.map((key) => {
    const label = getTranslation(key);

    return {
      label,
      value: optionsEnum[key],
    };
  });

  if (placeholderGroupKey === null) {
    return options;
  }

  const sortedOptions = fieldName === 'countryCode' ? prioritizeIreland(options) : options;
  return [getPlaceholderOption(translationFile, fieldName, placeholderGroupKey), ...sortedOptions];
};

export const createYesNoOptionsList = (
  translationFile: string,
  fieldName: string,
  placeholderGroupKey: string | null = 'fields'
): ReturnType<typeof createOptionsList> => {
  return createOptionsList(NoYesEnum, translationFile, fieldName, placeholderGroupKey);
};

/**
 * - Create simple options for SelectFormField input based on array.
 * - This method assumes that each value of array is both value and translation for given item.
 * - If you have proper enum use `createOptionsList` and create suitable translation texts in json file.
 * @param placeholderText Either boolean (for default placeholde) or text with custom one.
 */
export const createOptionsListFromArray = <T extends ReadonlyArray<string | number> | Array<string | number>>(
  arr: T
): Array<{ label: string; value: any }> => {
  return arr.map((value) => ({ label: String(value), value }));
};

/**
 * - Provides option list for enum where keys can be used as labels.
 */
export const createOptionsListFromTranslatedEnum = <T extends Record<string, string>>(
  enumeration: T
): Array<{ label: string; value: string }> => {
  return Object.keys(enumeration).map((key) => ({ label: String(key), value: enumeration[key] }));
};

/**
 * Similar to createOptionsList, but in format suitable for combobox component and without placeholder
 */
export const createComboboxList = <OE extends Record<any, any>>(
  optionsEnum: OE,
  translationFile: string,
  fieldName: string
) => {
  // Objects representing TS enums can have some extra keys for fields with numeric values,
  // so we filter them out.
  const keys = getEnumKeys(optionsEnum);

  const options = keys.map((key) => {
    const translationKey = `${translationFile}:options:${fieldName}:${key}`;
    const label = t(translationKey);

    if (!i18next.exists(translationKey)) {
      throw new Error(`Translation key (${translationKey}) not resolved. `);
    }

    return {
      value: label,
      key: optionsEnum[key],
    };
  });

  return options;
};

const createCustomerCheckerObj = (
  customerType: CustomerTypeEnum | undefined,
  businessType: BusinessCustomerTypesEnum | undefined,
  productFamily: ProductFamilyEnum | undefined
): Record<FlowTypeEnum, boolean> => {
  return {
    business: customerType === CustomerTypeEnum.business,
    consumer: customerType === CustomerTypeEnum.consumer,
    ltd: businessType === BusinessCustomerTypesEnum.ltd,
    plc: businessType === BusinessCustomerTypesEnum.plc,
    soleTrader: businessType === BusinessCustomerTypesEnum.soleTrader,
    partnership: businessType === BusinessCustomerTypesEnum.partnership,
    other: businessType === BusinessCustomerTypesEnum.other,
    unlimited: businessType === BusinessCustomerTypesEnum.unlimited,
    servicePlan: productFamily === ProductFamilyEnum.servicePlan,
    financialProduct: productFamily === ProductFamilyEnum.financialProduct,
    operatingLease: productFamily === ProductFamilyEnum.operatingLease,
    privateLease: productFamily === ProductFamilyEnum.privateLease,
  };
};

/**
 * - Function checks wheteher _any_ of `FlowTypeEnum` values provided as arguments list
 * is applicable to the current flow.
 */
export type FlowTypeCheckerFn = ((...args: Array<keyof typeof FlowTypeEnum>) => boolean) & {
  /**
   * - `every` enforces that each argument provided describes current application flow.
   */
  every: (...args: Array<keyof typeof FlowTypeEnum>) => boolean;
};

/**
 * - Creates function for easy checking which FlowType we are in.
 * @see {@link FlowTypeEnum}
 */
export const useFlowTypeChecker = (): FlowTypeCheckerFn => {
  const customerType = useSelector(journeyDataSelectors.customerType);
  const businessType = useSelector(journeyDataSelectors.businessCustomerType);
  const productFamily = useSelector(journeyDataSelectors.productFamily);

  const [checkerObject, setCheckerObject] = useState(() =>
    createCustomerCheckerObj(customerType, businessType, productFamily)
  );

  useEffect(() => {
    setCheckerObject(createCustomerCheckerObj(customerType, businessType, productFamily));
  }, [customerType, businessType, productFamily]);

  const checkerFnSome = useCallback(
    (...args: Parameters<FlowTypeCheckerFn>) => {
      return args.some((t) => checkerObject[t]);
    },
    [checkerObject]
  );

  const checkerFnEvery = useCallback(
    (...args: Parameters<FlowTypeCheckerFn>) => {
      return args.every((t) => checkerObject[t]);
    },
    [checkerObject]
  );

  const checkerFn = checkerFnSome;
  checkerFn['every'] = checkerFnEvery;

  return checkerFn as FlowTypeCheckerFn;
};

/**
 * - Wrapper for FieldArray render function that will make your `arrayHelpers.form.values` typed
 * according to provided model.
 * - E.g.:
 * `<FieldArray render={renderWithArrayHelpers<MyModel>((arrayHelpers) => {...}) />`
 */
export const renderWithArrayHelpers = <FormikState extends Record<string, any>>(
  handler: (arrayHelpers: ArrayHelpers<any> & { form: FormikProps<FormikState>; name: string }) => React.ReactNode
) => {
  return handler as (arrayHelpers: FieldArrayRenderProps) => React.ReactNode;
};

export const getDisabledFromFormState = (formInfo: TrackedFormInfo) => {
  return formInfo.excluded ? false : formInfo.isValidating || formInfo.isSubmitting;
};

export const getSuccessFromFormState = (formInfo: TrackedFormInfo) => {
  return formInfo.excluded ? true : formInfo.isValid && formInfo.submitCount > 0;
};

export const getErrorFromFormState = (formInfo: TrackedFormInfo) => {
  return formInfo.excluded ? false : !formInfo.isValid && formInfo.submitCount > 0;
};

/**
 * - Standard props for FormSection component to display
 * `disabled`, `success` and `error` states.
 */
export const getFormSectionStatusProps = (formInfo: TrackedFormInfo[] | TrackedFormInfo) => {
  if (Array.isArray(formInfo)) {
    return {
      disabled: formInfo.some((info) => getDisabledFromFormState(info)),
      success: undefined,
      error: undefined,
    };
  }

  return {
    disabled: getDisabledFromFormState(formInfo),
    success: undefined,
    error: undefined,
  };
};

const isSectionReady = (formInfo: ReduxTrackedForm<any> | TrackedFormInfo<any>) => {
  return formInfo.excluded || !(formInfo.isSubmitting || formInfo.isValidating);
};
const isSectionValid = (formInfo: ReduxTrackedForm<any> | TrackedFormInfo<any>) => {
  return formInfo.excluded || formInfo.isValid;
};

/**
 * - Helper function for components containing many FormSections allowing to
 * easily extract information about current section.
 * - See example usage in CustomerDataBusinessSoleTrader.tsx.
 */
export const checkIfSectionIsReadyAndValid = <SectionsEnum extends Record<any, any>>(
  currentSection: SectionsEnum[keyof SectionsEnum],
  config: Record<
    SectionsEnum[keyof SectionsEnum],
    ReduxTrackedForm<any> | TrackedFormInfo<any> | Array<ReduxTrackedForm<any>> | Array<TrackedFormInfo<any>>
  >
) => {
  if (Array.isArray(config[currentSection])) {
    return {
      currentSectionIsReady: (
        config[currentSection] as Array<ReduxTrackedForm<any>> | Array<TrackedFormInfo<any>>
      ).every(isSectionReady),
      currentSectionIsValid: (
        config[currentSection] as Array<ReduxTrackedForm<any>> | Array<TrackedFormInfo<any>>
      ).every(isSectionValid),
    };
  }

  return {
    currentSectionIsReady: isSectionReady(config[currentSection] as ReduxTrackedForm<any> | TrackedFormInfo<any>),
    currentSectionIsValid: isSectionValid(config[currentSection] as ReduxTrackedForm<any> | TrackedFormInfo<any>),
  };
};

export const isTestStage = (() => ['dev', 'int', 'cons'].includes(process.env.REACT_APP_STAGE as any))();

export const scrollIntoSection = (sectionId: string) => {
  setTimeout(() => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
    // Wait for animation end, then scroll to section,
    // 250ms is time of foldOut animation
  }, 251);
};

export const getFormSectionId = <T>(pageDesignator: string, section: T): string => {
  return `${pageDesignator}_${section}`;
};

export const setAllSections = <T extends Record<number, boolean>>(status: T, state = false) => {
  return Object.keys(status).reduce(
    (acc, key) => {
      const sectionKey = key;
      acc[sectionKey] = state;
      return acc;
    },
    {} as { T }
  );
};

export const isEnv = (...envsToCheckFor: Array<'dev' | 'int' | 'cons' | 'prod'>) => {
  return envsToCheckFor.includes(process.env.REACT_APP_STAGE || ('' as any));
};

export const isNotesEnabled = () => {
  return true;
};

export type DebugLogLevel = 'LOG' | 'ERROR';

export type DebugLog = {
  message: string;
  level?: DebugLogLevel;
};

export const debugLog = ({ message, level = 'LOG' }: DebugLog) => {
  if (isEnv('prod')) {
    return;
  }

  switch (level) {
    case 'ERROR':
      // eslint-disable-next-line no-console
      console.error(message);
      break;
    case 'LOG':
    default:
      // eslint-disable-next-line no-console
      console.log(message);
      break;
  }
};

export const isAtLeast3YearsApart = (date1: string, date2: string): boolean => {
  if (!date1 || !date2) return true;

  const format = 'dd/MM/yyyy';

  const date1Parsed = parse(date1, format, new Date());
  const date2Parsed = parse(date2, format, new Date());
  const diff = differenceInYears(date2Parsed, date1Parsed);

  return diff >= 3;
};

export const sumNumbersArray = (arr: number[]) => {
  return arr.reduce((total, value) => total + value, 0);
};

export const deduplicateStringsArray = <T extends string>(ar: T[]): T[] => {
  return ar.sort().filter((value, index, _ar) => {
    return _ar[index - 1] !== value;
  });
};

export const calculateYearsSinceDate = (dateString?: string): number | null => {
  if (!dateString) return null;

  const [day, month, year] = dateString.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();

  return differenceInYears(today, date);
};

export const sortObjectsArray = <T extends Record<string, any>>(ar: T[], getObjectValue: (o: T) => any): T[] => {
  return ar.sort((a, b) => getObjectValue(a) - getObjectValue(b));
};

export const initOptional = <T>(initialValue?: T) => {
  return initialValue ?? (undefined as T | undefined);
};

/**
 * - Use where concretizing given value type to not-undefined
 * is in line with required business logic.
 * - Does not return a value and makes TS assume that assertion will interrupt
 * parent function if value is not defined.
 */
export function /* assertion cannot be arrow fn */ assertDefinedVoid<T>(
  value: T | undefined,
  errorMessage?: string
): asserts value is T {
  if (value === undefined) {
    throw new Error(`Asserted valued was undefined. ${errorMessage ?? ''}`);
  }
}

/**
 * - Use where concretizing given value type to not-undefined
 * is in line with required business logic.
 */
export const assertDefined = <T>(value: T | undefined, errorMessage?: string): T => {
  if (value === undefined) {
    throw new Error(`Asserted valued was undefined. ${errorMessage ?? ''}`);
  }

  return value;
};
